import React, { useEffect, useState, useRef } from 'react';
import { Input } from '@/components/ui/inputs/input';
import { InputSelect } from '@/components/ui/inputs/input-select';
import { useForm, Controller } from 'react-hook-form';
import { getItemCategoryService } from '@/services/ItemCategoryServices';
import { getTaxService } from '@/services/TaxServices';
import validationErrors from '@/lib/validationErrors';
import { useSelector, useDispatch } from 'react-redux';

const DataTableFilter = ({ onChangeFilter }) => {
    const selectRef = useRef(null);
    const selectTaxRef = useRef(null);
    const selectItemRef = useRef(null);
    const selectItemTypeRef = useRef(null);
    const selectIsFeturesRef = useRef(null);
    const selectPerishableRef = useRef(null);

    const {
        register,
        setValue,
        getValues,
        formState: { errors },
        handleSubmit,
        control,
        reset,
    } = useForm({
        defaultValues: {
            name: '',
            price: '',
            item_category_id: '',
            tax_id: '',
            item_type: '',
            is_featured: '',
            status: '',
            isPerishable: '',   // new default for perishable filter
        },
        mode: 'onSubmit',
    });

    const dispatch = useDispatch();
    const [taxesOptionList, setTaxesOptionList] = useState([]);
    const [itemCategoriesOptionList, setItemCategories] = useState([]);
    const [isLoading, setIsLoading] = useState(false);

    const statusOptionList = [
        { value: 1, label: 'Active' },
        { value: 2, label: 'Inactive' },
    ];
    const isFeturesOptionList = [
        { value: 1, label: 'No' },
        { value: 2, label: 'Yes' },
    ];
    const itemTypeOptionList = [
        { value: 1, label: 'Veg' },
        { value: 2, label: 'Non Veg' },
    ];
    const perishableOptionList = [
        { value: true, label: 'Yes' },
        { value: false, label: 'No' },
    ];

    useEffect(() => {
        getItemCategory();
        getTaxes();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const convertUndefinedToBlank = (obj) => {
        return Object.fromEntries(
            Object.entries(obj).map(([key, value]) => [key, value === undefined ? '' : value])
        );
    };

    const clearSelection = () => {
        if (selectRef.current) {
            selectRef.current.clearValue();
        }
        if (selectTaxRef.current) {
            selectTaxRef.current.clearValue();
        }
        if (selectItemRef.current) {
            selectItemRef.current.clearValue();
        }
        if (selectItemTypeRef.current) {
            selectItemTypeRef.current.clearValue();
        }
        if (selectIsFeturesRef.current) {
            selectIsFeturesRef.current.clearValue();
        }
        if (selectPerishableRef.current) {
            selectPerishableRef.current.clearValue();
        }
    };

    const formReset = async () => {
        try {
            const formData = getValues();
            for (const key in formData) {
                await setValue(key, '');
            }
            await reset();
            await clearSelection();
            await onChangeFilter('extra_filter', {});
        } catch (error) {
            console.error('Error in onReset:', error);
        }
    };

    const getTaxes = () => {
        setIsLoading(true);
        dispatch(
            getTaxService({
                search: '',
                page: 1,
                limit: 100,
                extra_filter: {},
            })
        )
            .then((res) => {
                const taxesDataArr = res?.data?.data?.data || [];
                const taxesOptionArray = [];
                taxesDataArr.forEach((element) => {
                    taxesOptionArray.push({
                        value: element?._id,
                        label: element?.name,
                    });
                });
                setTaxesOptionList(taxesOptionArray);
                setIsLoading(false);
            })
            .catch(({ message, errorData, statusCode }) => {
                validationErrors(errorData, message);
                setIsLoading(false);
            });
    };

    const getItemCategory = () => {
        setIsLoading(true);
        dispatch(
            getItemCategoryService({
                search: '',
                page: 1,
                limit: 100,
                extra_filter: {},
            })
        )
            .then((res) => {
                const itemCategoryDataArr = res?.data?.data?.data || [];
                const itemCategoryOptionArray = [];
                itemCategoryDataArr.forEach((element) => {
                    itemCategoryOptionArray.push({
                        value: element?._id,
                        label: element?.name,
                    });
                });
                setItemCategories(itemCategoryOptionArray);
                setIsLoading(false);
            })
            .catch(({ message, errorData, statusCode }) => {
                validationErrors(errorData, message);
                setIsLoading(false);
            });
    };

    const onSubmit = async (data) => {
        try {
            // Convert undefined fields to blank, then call onChangeFilter
            const filterData = await convertUndefinedToBlank(data);
            await onChangeFilter('extra_filter', filterData);
        } catch (error) {
            console.error('Error in onSubmit:', error);
        }
    };

    return (
        <form onSubmit={handleSubmit(onSubmit)} className="p-4 sm:p-5 mb-5">
            <div className="row">
                {/* Name */}
                <div className="col-12 sm:col-6 md:col-4 xl:col-3">
                    <Controller
                        control={control}
                        name="name"
                        render={({ field }) => (
                            <Input
                                {...field}
                                size="sm"
                                labelText={'Name'}
                                type="text"
                                id="name"
                                className="db-field-control"
                                labelStyleClassName={'db-field-title after:hidden'}
                            />
                        )}
                    />
                </div>

                {/* Price */}
                <div className="col-12 sm:col-6 md:col-4 xl:col-3">
                    <Controller
                        control={control}
                        name="price"
                        render={({ field }) => (
                            <Input
                                {...field}
                                size="sm"
                                labelText={'Price'}
                                type="price"
                                id="price"
                                className="db-field-control"
                                labelStyleClassName={'db-field-title after:hidden'}
                            />
                        )}
                    />
                </div>

                {/* Category */}
                <div className="col-12 sm:col-6 md:col-4 xl:col-3">
                    <Controller
                        control={control}
                        name="item_category_id"
                        render={({ field }) => (
                            <InputSelect
                                {...field}
                                ref={selectItemRef}
                                id={'item_category_id'}
                                size="sm"
                                labelStyleClassName={'db-field-title after:hidden'}
                                labelText={'Category'}
                                value={
                                    itemCategoriesOptionList.find(
                                        (data) => data.value === getValues('item_category_id')
                                    ) || null
                                }
                                onChange={(e) => setValue('item_category_id', e)}
                                options={itemCategoriesOptionList}
                            />
                        )}
                    />
                </div>

                {/* Perishable filter (new) */}
                <div className="col-12 sm:col-6 md:col-4 xl:col-3">
                    <Controller
                        control={control}
                        name="isPerishable"
                        render={({ field }) => (
                            <InputSelect
                                {...field}
                                ref={selectPerishableRef}
                                id={'isPerishable'}
                                size="sm"
                                labelStyleClassName={'db-field-title after:hidden'}
                                labelText={'Perishable'}
                                value={
                                    perishableOptionList.find(
                                        (opt) => String(opt.value) === String(getValues('isPerishable'))
                                    ) || null
                                }
                                onChange={(e) => {
                                    // e.value might be boolean or string: ensure boolean or blank
                                    const val = e?.value;
                                    // If user clears selection, set to ''
                                    if (val === '' || val === null || val === undefined) {
                                        setValue('isPerishable', '');
                                    } else {
                                        // InputSelect likely returns {value: true/false}
                                        setValue('isPerishable', val);
                                    }
                                }}
                                options={perishableOptionList}
                            />
                        )}
                    />
                </div>

                {/* Tax */}
                <div className="col-12 sm:col-6 md:col-4 xl:col-3">
                    <Controller
                        control={control}
                        name="tax_id"
                        render={({ field }) => (
                            <InputSelect
                                {...field}
                                ref={selectTaxRef}
                                id={'tax_id'}
                                size="sm"
                                labelStyleClassName={'db-field-title after:hidden'}
                                labelText={'Tax'}
                                value={
                                    taxesOptionList.find(
                                        (data) => data.value === getValues('tax_id')
                                    ) || null
                                }
                                onChange={(e) => setValue('tax_id', e)}
                                options={taxesOptionList}
                            />
                        )}
                    />
                </div>

                {/* Status */}
                <div className="col-12 sm:col-6 md:col-4 xl:col-3">
                    <Controller
                        control={control}
                        name="status"
                        render={({ field }) => (
                            <InputSelect
                                {...field}
                                ref={selectRef}
                                id={'status'}
                                size="sm"
                                labelStyleClassName={'db-field-title after:hidden'}
                                labelText={'Status'}
                                value={
                                    statusOptionList.find(
                                        (data) => data.value === getValues('status')
                                    ) || null
                                }
                                onChange={(e) => setValue('status', e)}
                                options={statusOptionList}
                            />
                        )}
                    />
                </div>

                {/* Item Type */}
                <div className="col-12 sm:col-6 md:col-4 xl:col-3">
                    <Controller
                        control={control}
                        name="item_type"
                        render={({ field }) => (
                            <InputSelect
                                {...field}
                                ref={selectItemTypeRef}
                                id={'item_type'}
                                size="sm"
                                labelStyleClassName={'db-field-title after:hidden'}
                                labelText={'Item Type'}
                                value={
                                    itemTypeOptionList.find(
                                        (data) => data.value === getValues('item_type')
                                    ) || null
                                }
                                onChange={(e) => setValue('item_type', e)}
                                options={itemTypeOptionList}
                            />
                        )}
                    />
                </div>

                {/* Is Featured */}
                <div className="col-12 sm:col-6 md:col-4 xl:col-3">
                    <Controller
                        control={control}
                        name="is_featured"
                        render={({ field }) => (
                            <InputSelect
                                {...field}
                                ref={selectIsFeturesRef}
                                id={'is_featured'}
                                size="sm"
                                labelStyleClassName={'db-field-title after:hidden'}
                                labelText={'Is Featured'}
                                value={
                                    isFeturesOptionList.find(
                                        (data) => data.value === getValues('is_featured')
                                    ) || null
                                }
                                onChange={(e) => setValue('is_featured', e)}
                                options={isFeturesOptionList}
                            />
                        )}
                    />
                </div>

                {/* Submit / Clear */}
                <div className="col-12">
                    <div className="flex flex-wrap gap-3 mt-4">
                        <button type="submit" className="db-btn py-2 text-white bg-primary">
                            <i className="lab lab-search-line lab-font-size-16"></i>
                            <span>Search</span>
                        </button>
                        <button type="button" onClick={formReset} className="db-btn py-2 text-white bg-gray-600">
                            <i className="lab lab-cross-line-2 lab-font-size-22"></i>
                            <span>Clear</span>
                        </button>
                    </div>
                </div>
            </div>
        </form>
    );
};

export default DataTableFilter;
